import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsDepositBlocked, selectIsWithdrawBlocked } from '~/components/account/ducks';
import { NOTIFICATION_TYPE, sendAppBridgeEvent, showNativeNotification, WEB_EVENTS } from '~/components/app-bridge';
import { isNativeNotificationEnabled } from '~/components/app-bridge/app-properties';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';
import useTranslations from '~/components/translation/use-translations';
import { useSuspendedPlayerModal } from '~/components/payment/suspended-players-modal/hook/useSuspendedPlayerModal';

// Constants
import { OPERATION_TYPES, TEXT_KEYS } from '~/components/payment/suspended-players-modal/constants';

const NOTIFICATION_ID = 'suspended-player-notification';

// Define operation type based on existing constants
export type OperationType =
	| typeof OPERATION_TYPES.DEPOSIT
	| typeof OPERATION_TYPES.WITHDRAW
	| typeof OPERATION_TYPES.NONE
	| typeof OPERATION_TYPES.BOTH;

const useSuspendedPlayerCheck = () => {
	const dispatch = useDispatch();
	const { translations: t } = useTranslations(TEXT_KEYS);
	const [loading, setLoading] = useState(false);
	const { showSuspendedModal } = useSuspendedPlayerModal();

	const isDepositBlocked = useSelector(selectIsDepositBlocked);
	const isWithdrawBlocked = useSelector(selectIsWithdrawBlocked);

	const check = useCallback(
		async (operationType: OperationType = OPERATION_TYPES.BOTH) => {
			setLoading(true);

			try {
				const isBlocked =
					(operationType === OPERATION_TYPES.DEPOSIT && isDepositBlocked) ||
					(operationType === OPERATION_TYPES.WITHDRAW && isWithdrawBlocked) ||
					(operationType === OPERATION_TYPES.BOTH && (isDepositBlocked || isWithdrawBlocked));

				if (isBlocked) {
					// Determine which operation is actually blocked
					const actualOperation =
						operationType === OPERATION_TYPES.BOTH
							? isDepositBlocked
								? OPERATION_TYPES.DEPOSIT
								: OPERATION_TYPES.WITHDRAW
							: operationType;

					// Get the appropriate message based on operation type
					const message = t.PLAYER_SUSPENDED_MESSAGE || 'Your account has restrictions';

					if (isNativeNotificationEnabled()) {
						showNativeNotification(NOTIFICATION_TYPE.GENERAL_ERROR, message);
						sendAppBridgeEvent(WEB_EVENTS.LIMIT_MODAL_CLOSED);
					} else {
						// Show the suspension modal with the specific operation type
						showSuspendedModal({
							operationType: actualOperation,
							isDepositBlocked,
							isWithdrawBlocked
						});
					}

					return false;
				}

				return true;
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Suspended player check error:', error);

				const message = t.GENERAL_ERROR || 'An error occurred';

				if (isNativeNotificationEnabled()) {
					showNativeNotification(NOTIFICATION_TYPE.GENERAL_ERROR, message);
				} else {
					dispatch(
						showQueuedNotification({
							message,
							color: 'red',
							autoHide: true,
							openInSidebar: true,
							id: NOTIFICATION_ID
						})
					);
				}

				return false;
			} finally {
				setLoading(false);
			}
		},
		[dispatch, isDepositBlocked, isWithdrawBlocked, t, showSuspendedModal]
	);

	return {
		check,
		loading,
		isDepositBlocked,
		isWithdrawBlocked
	};
};

export default useSuspendedPlayerCheck;
